export const REPORTS_ALLOWED_SHOP_IDS = [4, 23];

export type ReportType = 'sales' | 'activePlans';

export interface SalesReportFilters {
  shopId: number;
  reportType?: ReportType;
  productId?: number | string;
  startDate?: string;
  endDate?: string;
  minAmount?: number | string;
  maxAmount?: number | string;
  saleState?: number | string;
  paymentTypeId?: number | string;
  paymentTypeMode?: number | string;
  shopPointId?: number | string;
  shopCompanyId?: number | string;
  productPaymentMode?: number | string;
  searchString?: string;
}

export interface SalesReportSummary {
  totalSales?: number | string | null;
  totalClients?: number | string | null;
  totalAssociations?: number | string | null;
  totalPrincipalAmount?: number | string | null;
  totalDiscountAmount?: number | string | null;
  totalNetAmount?: number | string | null;
  totalMonthlyInstallment?: number | string | null;
  totalPaidAmount?: number | string | null;
  calculatedPendingBalance?: number | string | null;
  totalPaymentsCount?: number | string | null;
  firstSaleDate?: string | null;
  lastSaleDate?: string | null;
}

export interface SalesReportSale {
  saleId?: number | string | null;
  saleOrderCode?: string | null;
  saleShortCode?: string | null;
  saleDate?: string | null;
  saleEndPrice?: number | string | null;
  saleState?: number | string | null;
  clientNationalId?: string | number | null;
  nationalId?: string | number | null;
  identification?: string | number | null;
  clientName?: string | null;
  name?: string | null;
  shopCompanyName?: string | null;
  companyName?: string | null;
  association?: string | null;
  principalAmount?: number | string | null;
  termMonths?: number | string | null;
  firstInstallmentDate?: string | null;
  monthlyInstallment?: number | string | null;
  observations?: string | null;
  products?: string | null;
  [key: string]: unknown;
}

export interface SalesReportItem {
  saleId?: number | string | null;
  itemId?: number | string | null;
  productName?: string | null;
  quantity?: number | string | null;
  [key: string]: unknown;
}

export interface SalesReportExcelRow {
  identification?: string | number | null;
  name?: string | null;
  document?: string | number | null;
  date?: string | null;
  principalAmount?: number | string | null;
  discountAmount?: number | string | null;
  netAmount?: number | string | null;
  termMonths?: number | string | null;
  firstInstallmentDate?: string | null;
  monthlyInstallment?: number | string | null;
  observations?: string | null;
  [key: string]: unknown;
}

export interface SalesReportResponse {
  reportType?: ReportType;
  summary: SalesReportSummary;
  sales: SalesReportSale[];
  items: SalesReportItem[];
  excelRows: SalesReportExcelRow[];
  filters: SalesReportFilters;
}

export interface SalesReportPreviewRow {
  identification: string;
  client: string;
  order: string;
  date: string;
  association: string;
  plan?: string;
  planCode?: string;
  shopPoint?: string;
  quantity?: number;
  status?: string;
  principalAmount: number;
  termMonths: number | null;
  firstInstallmentDate: string;
  monthlyInstallment: number;
  observations: string;
}

export interface ValidatedSalesReportExcelRow extends SalesReportExcelRow {
  identification: string;
  name: string;
  date: string;
  principalAmount: number;
  discountAmount: number;
  netAmount: number;
  termMonths: number;
  firstInstallmentDate: string;
  monthlyInstallment: number;
  observations: string;
}

export interface ReportCatalogOption {
  id: number | string;
  name: string;
}

export interface BillingReportQuery {
  action: 'preview' | 'history' | 'getReport';
  shopId: number | string;
  startDate?: string | null;
  endDate?: string | null;
  shopCompanyId?: number | string | null;
  amount?: number | string | null;
  billingReportId?: number | string | null;
}

export interface GenerateBillingReportPayload {
  shopId: number | string;
  startDate: string;
  endDate: string;
  shopCompanyId: number | string;
  createdBy: string;
}

export interface BillingReportSummary {
  totalRows?: number | string | null;
  totalClients?: number | string | null;
  totalAssociations?: number | string | null;
  totalInstallmentRows?: number | string | null;
  totalRecurringRows?: number | string | null;
  totalPrincipalAmount?: number | string | null;
  totalMonthlyInstallment?: number | string | null;
}

export interface BillingReportHeader extends BillingReportSummary {
  billingReportId?: number | string | null;
  shopId?: number | string | null;
  startDate?: string | null;
  endDate?: string | null;
  shopCompanyId?: number | string | null;
  shopCompanyName?: string | null;
  existingBillingReportId?: number | string | null;
  existingStartDate?: string | null;
  existingEndDate?: string | null;
  status?: 'PREVIEW' | 'ALREADY_GENERATED' | 'GENERATED' | 'EMPTY' | string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  message?: string | null;
}

export interface BillingReportRow {
  billingReportItemId?: number | string | null;
  billingReportId?: number | string | null;
  shopId?: number | string | null;
  saleId?: number | string | null;
  itemId?: number | string | null;
  productId?: number | string | null;
  billingType?: 'RECURRING_MONTHLY' | 'INSTALLMENT' | string | null;
  exportScope?: string | null;
  recurrence?: string | null;
  termMonths?: number | string | null;
  clientNationalId?: string | number | null;
  clientFullName?: string | null;
  saleOrderCode?: string | null;
  saleDate?: string | null;
  saleDateOnly?: string | null;
  productCode?: string | null;
  productName?: string | null;
  itemCode?: string | null;
  quantity?: number | string | null;
  unitCost?: number | string | null;
  principalAmount?: number | string | null;
  discountAmount?: number | string | null;
  netAmount?: number | string | null;
  monthlyInstallment?: number | string | null;
  firstInstallmentDate?: string | null;
  shopCompanyId?: number | string | null;
  shopCompanyName?: string | null;
  shopPointId?: number | string | null;
  shopPointName?: string | null;
  reportObservations?: string | null;
  createdAt?: string | null;
}

export interface BillingReportResponse {
  action?: 'preview' | 'generate' | 'getReport' | string;
  billingReportId?: number | string | null;
  report?: BillingReportHeader | null;
  summary?: BillingReportSummary;
  rows: BillingReportRow[];
  excelRows: SalesReportExcelRow[];
  message?: string | null;
}

export interface BillingReportHistoryItem extends BillingReportSummary {
  billingReportId?: number | string | null;
  shopId?: number | string | null;
  startDate?: string | null;
  endDate?: string | null;
  shopCompanyId?: number | string | null;
  shopCompanyName?: string | null;
  status?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
}

export interface BillingReportHistoryResponse {
  action?: 'history' | string;
  shopId?: number | string | null;
  reports: BillingReportHistoryItem[];
}
